import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AppointmentCreate } from '../models/appointment-model';
import { PatientService } from './patient.service';
import { CalendarUtilsService } from '../pages/calendar-page/calendar-utils.service';
import { ErrorPanelService } from './error-panel.service';
import { DoctorService } from './doctor.service';
import { DoctorPersonalArea } from '../models/doctor-model';

declare const gapi: any;

@Injectable({
  providedIn: 'root',
})
export class GmailService {
  supportEmailAddress = 'amministrazione@psicogenius.it';

  constructor(
    private patientService: PatientService,
    private calendarUtils: CalendarUtilsService,
    private errorPanelService: ErrorPanelService,
    private doctorService: DoctorService
  ) {}

  /**
   * Sends an email to the support email address
   * @param fromEmail - The sender's email address
   * @param body - The email body content
   * @returns Observable<boolean> - True if email sent successfully
   */
  sendEmailToSupport(body: string): Observable<boolean> {
    return from(
      new Promise<boolean>((resolve, reject) => {
        gapi.load('client', () => {
          gapi.client.setApiKey(environment.googleAPIKey);
          gapi.client.load('gmail', 'v1', () => {
            const token = sessionStorage.getItem('googleAccessToken');
            gapi.auth.setToken({ access_token: token });

            const email = [
              'Content-Type: text/plain; charset="UTF-8"\n',
              'MIME-Version: 1.0\n',
              'Content-Transfer-Encoding: 7bit\n',
              'to: ',
              this.supportEmailAddress,
              '\n',
              'subject: Richiesta di supporto\n\n',
              body,
            ].join('');

            const base64EncodedEmail = btoa(email)
              .replace(/\+/g, '-')
              .replace(/\//g, '_')
              .replace(/=+$/, '');

            gapi.client.gmail.users.messages
              .send({
                userId: 'me',
                resource: {
                  raw: base64EncodedEmail,
                },
              })
              .then(() => {
                this.errorPanelService.showInfoSnackBar(
                  'La richiesta è stata inviata con successo. Verrai contattato a breve dal nostro team.'
                );
                resolve(true);
              })
              .catch((error: any) => {
                console.error('Error sending email:', error);
                reject(error);
              });
          });
        });
      })
    ).pipe(
      map(() => true),
      catchError((error) => {
        console.error('Error sending email:', error);
        throw error;
      })
    );
  }

  sendEmailToClient(
    emailAddress: string,
    body: string,
    subject: string
  ): Observable<boolean> {
    return from(
      new Promise<boolean>((resolve, reject) => {
        gapi.load('client', () => {
          gapi.client.setApiKey(environment.googleAPIKey);
          gapi.client.load('gmail', 'v1', () => {
            const token = sessionStorage.getItem('googleAccessToken');
            gapi.auth.setToken({ access_token: token });

            const email = [
              'Content-Type: text/plain; charset="UTF-8"\n',
              'MIME-Version: 1.0\n',
              'Content-Transfer-Encoding: 7bit\n',
              'to: ',
              emailAddress,
              '\n',
              'subject: ',
              subject,
              '\n\n',
              body,
            ].join('');

            const base64EncodedEmail = btoa(email)
              .replace(/\+/g, '-')
              .replace(/\//g, '_')
              .replace(/=+$/, '');

            gapi.client.gmail.users.messages
              .send({
                userId: 'me',
                resource: {
                  raw: base64EncodedEmail,
                },
              })
              .then(() => {
                this.errorPanelService.showInfoSnackBar(
                  'Email di promemoria inviata con successo al paziente.'
                );
                resolve(true);
              })
              .catch((error: any) => {
                console.error('Error sending email:', error);
                reject(error);
              });
          });
        });
      })
    ).pipe(
      map(() => true),
      catchError((error) => {
        console.error('Error sending email:', error);
        throw error;
      })
    );
  }

  prepareEmailForClient(appointment: AppointmentCreate) {
    const appointmentDate =
      this.calendarUtils.convertStringDateAndTimeToDateObject(
        appointment.date,
        appointment.start_time
      );
    const currentDate = new Date();

    if (appointmentDate < currentDate) {
      console.log('Appointment date is in the past');
      return;
    }

    this.patientService.getPatient(appointment.patient_id).subscribe({
      next: (patient) => {
        const patientEmail = patient.email;
        if (patientEmail && patientEmail !== '') {
          this.doctorService.getDoctorPersonalArea().subscribe({
            next: (doctor: DoctorPersonalArea) => {
              const emailSubject = `Promemoria Appuntamento con il dott./dott.ssa ${doctor.first_name} ${doctor.last_name} - ${appointment.date}`;
              const emailBody = `Gentile ${patient.first_name} ${patient.last_name}, \nle ricordo il nostro appuntamento fissato per il giorno ${appointment.date} alle ${appointment.start_time}. \nUn cordiale saluto, \n${doctor.first_name} ${doctor.last_name}`;
              this.sendEmailToClient(patientEmail, emailBody, emailSubject);
            },
            error: (error) => {
              console.error('Error:', error);
              this.errorPanelService.showErrorSnackBar(
                'Errore nel recuperare i dati'
              );
            },
          });
        }
      },
      error: (error) => {
        console.error('Error getting patient:', error);
      },
    });
  }

  /**
   * Sends an email with PDF attachment to a client
   * @param toEmail - The recipient's email address
   * @param subject - Email subject
   * @param body - Email body content
   * @param pdfBlob - The PDF attachment as a Blob
   * @returns Observable<boolean> - True if email sent successfully
   */
  sendEmailWithPDFAttachment(
    toEmail: string,
    subject: string,
    body: string,
    pdfBlob: Blob
  ): Observable<boolean> {
    return from(
      new Promise<boolean>((resolve, reject) => {
        // Convert PDF blob to base64
        const reader = new FileReader();
        reader.readAsDataURL(pdfBlob);
        reader.onload = () => {
          const base64Pdf = reader.result?.toString().split(',')[1];

          gapi.load('client', () => {
            gapi.client.setApiKey(environment.googleAPIKey);
            gapi.client.load('gmail', 'v1', () => {
              const token = sessionStorage.getItem('googleAccessToken');
              gapi.auth.setToken({ access_token: token });

              // Create email with attachment
              const email = [
                'Content-Type: multipart/mixed; boundary="boundary"\n',
                'MIME-Version: 1.0\n',
                'to: ',
                toEmail,
                '\n',
                'subject: ',
                subject,
                '\n\n',
                '--boundary\n',
                'Content-Type: text/plain; charset="UTF-8"\n',
                'MIME-Version: 1.0\n',
                'Content-Transfer-Encoding: 7bit\n\n',
                body,
                '\n\n',
                '--boundary\n',
                'Content-Type: application/pdf\n',
                'MIME-Version: 1.0\n',
                'Content-Transfer-Encoding: base64\n',
                'Content-Disposition: attachment; filename="fattura.pdf"\n\n',
                base64Pdf,
                '\n\n',
                '--boundary--',
              ].join('');

              const base64EncodedEmail = btoa(email)
                .replace(/\+/g, '-')
                .replace(/\//g, '_')
                .replace(/=+$/, '');

              gapi.client.gmail.users.messages
                .send({
                  userId: 'me',
                  resource: {
                    raw: base64EncodedEmail,
                  },
                })
                .then(() => {
                  this.errorPanelService.showInfoSnackBar(
                    'Email inviata con successo.'
                  );
                  resolve(true);
                })
                .catch((error: any) => {
                  console.error('Error sending email:', error);
                  reject(error);
                });
            });
          });
        };
        reader.onerror = (error) => reject(error);
      })
    ).pipe(
      map(() => true),
      catchError((error) => {
        console.error('Error sending email:', error);
        throw error;
      })
    );
  }
}
